import React, { useEffect, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  CardHeader,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import Select from 'react-select';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  useCreateMutation,
  useUpdateMutation,
  useDetailQuery,
  useUpdatePatchMutation,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { handleFormErrors } from 'utils/formErrorHandler';
import FormField from 'components/Form/Inputs/FormField';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useShop } from 'context/shopContext';
import { ShopCategory } from 'components/Types/Tenant/shop.type';

// Define the form values type based on ItemUnit entity
interface CreateItemUnitFormValues {
  unitName: string;
  shopCategory: ShopCategory; // ShopCategory enum as string for form
  notes?: string;
  isActive: boolean;
}

// Strongly typed fields
const formFields: {
  id: keyof CreateItemUnitFormValues;
  label: string;
  required?: boolean;
  disabled?: boolean;
  type?: string;
}[] = [
  { id: 'unitName', label: 'Unit Name', required: true },
  { id: 'shopCategory', label: 'Shop Category', type: 'select', disabled: true },
  { id: 'notes', label: 'Notes', type: 'textarea' },
  { id: 'isActive', label: 'Is Active', type: 'checkbox' },
];

const shopCategoryOptions = Object.values(ShopCategory).map((category) => ({
  value: category,
  label: category,
}));

// Yup validation schema
const validationSchema = Yup.object({
  unitName: Yup.string()
    .trim()
    .required('Unit Name is required')
    .max(50, 'Unit Name must be at most 50 characters'),
  shopCategory: Yup.string()
    .oneOf(Object.values(ShopCategory), 'Invalid shop category')
    .required('Shop Category is required'),
  notes: Yup.string().trim().max(500, 'Notes must be at most 500 characters').nullable(),
  isActive: Yup.boolean(),
});

// Pass id as props
const AddUpdateItemUnitForm: React.FC<{ id?: string }> = ({ id }) => {
  const { shop } = useShop();
  const { id: shopId } = shop!;
  const isEditMode = Boolean(id);

  const navigate = useNavigate();
  // Fetch existing item unit data for edit mode
  const { data: fetchedItemUnit, isLoading: loadingItemUnit } =
    useDetailQuery<CreateItemUnitFormValues>(API_ENDPOINTS.TENANT.ITEM_UNITS.WITH_ID, {
      id: id ?? '',
    });

  const { mutate: createItemUnit, isPending: creating } = useCreateMutation(
    API_ENDPOINTS.TENANT.ITEM_UNITS.BASE,
  );
  const { mutate: updateItemUnit, isPending: updating } = useUpdatePatchMutation(
    API_ENDPOINTS.TENANT.ITEM_UNITS.WITH_ID,
    API_ENDPOINTS.TENANT.ITEM_UNITS.BASE,
  );

  // Initial values - empty or from fetched data
  const initialValues: CreateItemUnitFormValues = {
    unitName: '',
    shopCategory: ShopCategory.Pharmacy,
    notes: '',
    isActive: true,
    ...fetchedItemUnit,
  };

  const formik = useFormik<CreateItemUnitFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<CreateItemUnitFormValues>) => {
      const payload = {
        ...values,
        unitName: values.unitName.trim(),
        notes: values.notes?.trim() || undefined,
        shopCategory: values.shopCategory || undefined,
      };

      const onSuccess = () => {
        navigate(SHOP_PATHS.ITEM_UNITS.replace(':shopId', shopId || ''));
      };

      const onError = (error: any) => {
        handleFormErrors<CreateItemUnitFormValues>(error, formikHelpers);
      };

      if (isEditMode) {
        updateItemUnit({ data: values, params: { id: id } }, { onSuccess, onError });
      } else {
        createItemUnit(payload, { onSuccess, onError });
      }
    },
  });

  const handleShopCategoryChange = (option: { value: string; label: string } | null) => {
    formik.setFieldValue('shopCategory', option ? option.value : undefined);
  };

  if (isEditMode && loadingItemUnit) {
    return (
      <Container className="py-5 text-center">
        <Spinner color="primary" />
      </Container>
    );
  }

  return (
    <Row className="justify-content-center">
      <Col md={12} lg={8}>
        <Card className="p-4 shadow-sm">
          <CardHeader>
            <h2 className="text-center fw-bold fs-2">
              {isEditMode ? 'Update Item Unit' : 'Add a New Item Unit'}
            </h2>
          </CardHeader>

          <CardBody>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Row className="mt-0">
                {formFields.map((field) => (
                  <Col key={field.id} sm={12} md={6} lg={6}>
                    <FormField
                      id={field.id}
                      label={field.label}
                      type={field.type}
                      required={field.required}
                      disabled={field.disabled}
                      value={formik.values[field.id]}
                      touched={formik.touched[field.id]}
                      error={formik.errors[field.id]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      options={field.type === 'select' ? shopCategoryOptions : undefined}
                      onSelectChange={
                        field.type === 'select' ? handleShopCategoryChange : undefined
                      }
                    />
                  </Col>
                ))}
              </Row>

              <div className="d-flex justify-content-center mt-4">
                <Button color="primary" type="submit" disabled={creating || updating}>
                  {creating || updating
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                    ? 'Update Item Unit'
                    : 'Add Item Unit'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default memo(AddUpdateItemUnitForm);
